import React, { useState, useEffect } from "react";
import { Grid, FormControl, Select, MenuItem, InputLabel } from "@material-ui/core";
import { DateSelect } from "components";
import { Club } from "api/clubs";
import { AudienceGroup } from "api/bookings";
import i18n from "./i18n";

// prop types
interface AudienceSelectProps {
    club: Club | null,
    defaultValue?: AudienceGroup,
    onChange: (value: AudienceGroup) => void,
}

// gender types
const genderTypes = ["male", "female", "diverse", "none"] as const;

// audience selection & filter
const AudienceSelect = ({ club, defaultValue, onChange }: AudienceSelectProps) => {
    // ui state
    const [gender, setGender] = useState<typeof genderTypes[number] | "">("");
    const [bornAfter, setBornAfter] = useState<string>();
    const [bornBefore, setBornBefore] = useState<string>();
    const [clubId, setClubId] = useState("");

    // prefill defaults
    useEffect(() => {
        setBornAfter(defaultValue?.bornAfter);
        setBornBefore(defaultValue?.bornBefore);
        setClubId(defaultValue?.clubId || "");
        setGender(defaultValue?.gender || "");
    }, []); // eslint-disable-line

    // send change event
    useEffect(() => onChange({
        bornAfter: bornAfter || undefined,
        bornBefore: bornBefore || undefined,
        clubId: clubId || undefined,
        gender: gender || undefined,
    }), [bornAfter, bornBefore, clubId, gender]); // eslint-disable-line

    // render
    return (
        <Grid container spacing={2}>

            {/* gender */}
            <Grid item xs={6}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel>{i18n.gender}</InputLabel>
                    <Select
                        label={i18n.gender}
                        value={gender}
                        onChange={(e) => setGender(e.target.value as typeof gender)}
                    >
                        <MenuItem value=""><em>{`(${i18n.all})`}</em></MenuItem>
                        {genderTypes.map((g) => (
                            <MenuItem value={g} key={g}>{i18n.genders[g]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {/* members of club */}
            <Grid item xs={6}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel>{i18n.memberOfClub}</InputLabel>
                    <Select
                        label={i18n.memberOfClub}
                        value={clubId}
                        onChange={(e) => setClubId(e.target.value as string)}
                    >
                        <MenuItem value=""><em>{`(${i18n.all})`}</em></MenuItem>
                        <MenuItem value={club?.id}>{club?.clubName}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            {/* born before & after */}
            <Grid item xs={6}>
                <DateSelect
                    label={i18n.bornBefore}
                    value={bornBefore}
                    variant="outlined"
                    onChange={(m) => setBornBefore(m?.startOf("day").toISOString())}
                />
            </Grid>
            <Grid item xs={6}>
                <DateSelect
                    label={i18n.bornAfter}
                    value={bornAfter}
                    variant="outlined"
                    onChange={(m) => setBornAfter(m?.startOf("day").toISOString())}
                />
            </Grid>
        </Grid>
    );
};

export default AudienceSelect;
